/**
 * Реестр типов узлов ArchiMate для X6.
 * По нормализованному имени типа возвращает экземпляр класса-описателя узла
 * (свойства фигуры, правила соединений и вложенности).
 */
import {ARCHIMATE_GROUP} from '../metamodel/Concept';
import {
	AndJunctionNodeType,
	OrJunctionNodeType,
	NoteNodeType,
	GroupNodeType,
	GroupingNodeType,
} from './other';
import {
	ApplicationComponentNodeType,
	ApplicationCollaborationNodeType,
	ApplicationInterfaceNodeType,
	ApplicationFunctionNodeType,
	ApplicationInteractionNodeType,
	ApplicationProcessNodeType,
	ApplicationEventNodeType,
	ApplicationServiceNodeType,
	DataObjectNodeType,
} from './application';
import {
	ResourceNodeType,
	CapabilityNodeType,
	ValueStreamNodeType,
	CourseOfActionNodeType,
} from './strategy';
import {
	AssessmentNodeType,
	ConstraintNodeType,
	DriverNodeType,
	GoalNodeType,
	MeaningNodeType,
	OutcomeNodeType,
	PrincipleNodeType,
	RequirementNodeType,
	StakeholderNodeType,
	ValueNodeType,
} from './motivation';
import {
	BusinessActorNodeType,
	BusinessRoleNodeType,
	BusinessCollaborationNodeType,
	BusinessInterfaceNodeType,
	BusinessProcessNodeType,
	BusinessFunctionNodeType,
	BusinessInteractionNodeType,
	BusinessEventNodeType,
	BusinessServiceNodeType,
	BusinessObjectNodeType,
	ContractNodeType,
	RepresentationNodeType,
	ProductNodeType,
} from './business';
import {
	TechnologyNodeNodeType,
	DeviceNodeType,
	SystemSoftwareNodeType,
	TechnologyCollaborationNodeType,
	TechnologyInterfaceNodeType,
	PathNodeType,
	CommunicationNetworkNodeType,
	TechnologyFunctionNodeType,
	TechnologyProcessNodeType,
	TechnologyInteractionNodeType,
	TechnologyEventNodeType,
	TechnologyServiceNodeType,
	ArtifactNodeType,
} from './technology';
import {
	DistributionNetworkNodeType,
	EquipmentNodeType,
	FacilityNodeType,
	MaterialNodeType,
} from './physical';
import {
	DeliverableNodeType,
	GapNodeType,
	ImplementationEventNodeType,
	PlateauNodeType,
	WorkPackageNodeType,
} from './impMig';
import {LocationNodeType} from './other';
import {StickerNodeType} from './canvas';

function normalizeType(type) {
	if (!type) return '';
	const base = String(type).split(':').pop();
	if (base.includes('-')) {
		return base
			.split('-')
			.map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
			.join('');
	}
	if (base.includes('_')) {
		return base
			.split('_')
			.map(chunk => chunk.charAt(0).toUpperCase() + chunk.slice(1))
			.join('');
	}
	return base.charAt(0).toUpperCase() + base.slice(1);
}

class NodeTypeRegistry {
	constructor() {
		this._map = new Map();
		this._register(
			new AndJunctionNodeType(),
			new OrJunctionNodeType(),
			new NoteNodeType(),
			new GroupNodeType(),
			new GroupingNodeType(),
			// Application
			new ApplicationComponentNodeType(),
			new ApplicationCollaborationNodeType(),
			new ApplicationInterfaceNodeType(),
			new ApplicationFunctionNodeType(),
			new ApplicationInteractionNodeType(),
			new ApplicationProcessNodeType(),
			new ApplicationEventNodeType(),
			new ApplicationServiceNodeType(),
			new DataObjectNodeType(),
			// Strategy
			new ResourceNodeType(),
			new CapabilityNodeType(),
			new ValueStreamNodeType(),
			new CourseOfActionNodeType(),
			// Motivation
			new AssessmentNodeType(),
			new ConstraintNodeType(),
			new DriverNodeType(),
			new GoalNodeType(),
			new MeaningNodeType(),
			new OutcomeNodeType(),
			new PrincipleNodeType(),
			new RequirementNodeType(),
			new StakeholderNodeType(),
			new ValueNodeType(),
			// Business
			new BusinessActorNodeType(),
			new BusinessRoleNodeType(),
			new BusinessCollaborationNodeType(),
			new BusinessInterfaceNodeType(),
			new BusinessProcessNodeType(),
			new BusinessFunctionNodeType(),
			new BusinessInteractionNodeType(),
			new BusinessEventNodeType(),
			new BusinessServiceNodeType(),
			new BusinessObjectNodeType(),
			new ContractNodeType(),
			new RepresentationNodeType(),
			new ProductNodeType(),
			// Technology
			new TechnologyNodeNodeType(),
			new DeviceNodeType(),
			new SystemSoftwareNodeType(),
			new TechnologyCollaborationNodeType(),
			new TechnologyInterfaceNodeType(),
			new PathNodeType(),
			new CommunicationNetworkNodeType(),
			new TechnologyFunctionNodeType(),
			new TechnologyProcessNodeType(),
			new TechnologyInteractionNodeType(),
			new TechnologyEventNodeType(),
			new TechnologyServiceNodeType(),
			new ArtifactNodeType(),
			// Physical
			new DistributionNetworkNodeType(),
			new EquipmentNodeType(),
			new FacilityNodeType(),
			new MaterialNodeType(),
			// Implementation & Migration
			new DeliverableNodeType(),
			new GapNodeType(),
			new ImplementationEventNodeType(),
			new PlateauNodeType(),
			new WorkPackageNodeType(),
			// Other
			new LocationNodeType(),
			new StickerNodeType()
		);
		// Group: typeName у GroupNodeType — ARCHIMATE_GROUP ('archimate:Group'), а normalizeType даёт 'Group'
		const groupInstance = this._map.get(ARCHIMATE_GROUP);
		if (groupInstance) {
			this._map.set('Group', groupInstance);
		}
	}

	_register(...instances) {
		instances.forEach(inst => {
			const name = inst.constructor.typeName;
			if (name) this._map.set(name, inst);
		});
	}

	/**
	 * Возвращает описатель типа узла по нормализованному имени типа.
	 * @param {string} type — тип элемента (например 'ApplicationComponent', 'Group', 'archimate:Group')
	 * @returns {ArchimateNodeType|null}
	 */
	get(type) {
		const normalized = normalizeType(type);
		return this._map.get(normalized) || null;
	}

	/**
	 * Регистрирует свой класс типа узла (инстанс).
	 * @param {ArchimateNodeType} instance
	 */
	register(instance) {
		const name = instance.constructor.typeName;
		if (name) this._map.set(name, instance);
	}
}

export const nodeTypeRegistry = new NodeTypeRegistry();

function getNodeGraphBBox(cell, cellView) {
	if (cellView && typeof cellView.getBBox === 'function') {
		try {
			return cellView.getBBox();
		} catch {
			// fallback
		}
	}
	return cell?.getBBox?.();
}

/**
 * findParent через CellView.getBBox(): у вложенных узлов model.getBBox() — координаты
 * относительно родителя, встроенный 'bbox' после загрузки иерархии не находит кандидатов.
 */
export function getEmbeddingFindParent() {
	return function findParent({view, node}) {
		const graph = this;
		if (!graph || !view || !node) return [];

		let childBBox;
		try {
			childBBox = view.getBBox();
		} catch {
			return [];
		}
		if (!childBBox) return [];

		return graph.getNodes().filter(candidate => {
			if (candidate.id === node.id) return false;
			if (typeof candidate.isDescendantOf === 'function' && candidate.isDescendantOf(node)) {
				return false;
			}
			if (!candidate.visible) return false;

			const candidateView = graph.findViewByCell(candidate);
			const parentBBox = getNodeGraphBBox(candidate, candidateView);
			return (
				parentBBox &&
				typeof parentBBox.isIntersectWithRect === 'function' &&
				parentBBox.isIntersectWithRect(childBBox)
			);
		});
	};
}

/**
 * Возвращает функцию validate для embedding (можно ли вложить child в parent).
 * X6 вызывает validate с одним аргументом: { child, parent, childView, parentView }.
 * Используем canBeParent у типа родительского узла (из data.elementType).
 * @returns {(payload: { child: import('@antv/x6').Node, parent: import('@antv/x6').Node, childView?: unknown, parentView?: unknown }) => boolean}
 */
export function getEmbeddingValidate() {
	return function validate(payload) {
		const parent = payload.parent;
		const child = payload.child;
		if (!parent || !child) return false;

		const elementType = parent.getData()?.elementType;
		const nodeType = elementType ? nodeTypeRegistry.get(elementType) : null;
		if (nodeType?.canBeParent === true) {
			const bbox = getNodeGraphBBox(child, payload.childView);
			const targetBBox = getNodeGraphBBox(parent, payload.parentView);
			if (!bbox || !targetBBox) return false;

			return targetBBox.containsRect(bbox) && bbox.isIntersectWithRect(targetBBox);
		}

		return false;
	};
}

export default NodeTypeRegistry;
